import React, { useEffect, useMemo, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link, useNavigate } from "react-router-dom";
import { MegaphoneIcon as Megaphone, QuotesIcon as Quotes, SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight, ArrowSquareOutIcon as ArrowSquareOut, ShieldCheckIcon as ShieldCheck, ReceiptIcon as Receipt, GitForkIcon as GitFork, StorefrontIcon as Storefront, HeartIcon as Heart, DogIcon as Dog, GlobeHemisphereWestIcon as GlobeHemisphereWest, CopySimpleIcon as CopySimple, CheckCircleIcon as CheckCircle, ScalesIcon as Scales, FingerprintIcon as Fingerprint, PrinterIcon as Printer, DownloadSimpleIcon as DownloadSimple, LockKeyIcon as LockKey, VideoCameraIcon as VideoCamera, EnvelopeSimpleIcon as EnvelopeSimple, ShareNetworkIcon as ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";

/**
 * Admin-only press kit hub.
 *
 * The press kit is no longer exposed publicly. Admins land here from
 * the Admin Console, review the live stats and pre-written copy, and
 * choose what to share — by clicking "Print / Save as PDF" or
 * "Download fact sheet (.txt)".
 *
 * Pulls live stats from /api/admin/press/stats (admin auth required).
 */
const PITCHES = {
  ten: {
    label: "10-second elevator",
    duration: "≈ 10 seconds",
    body: "Armstrong K9 Registry — a trusted online marketplace and pedigree platform for purebred and exotic dogs, where serious buyers can find verified breeders, see complete bloodlines, and pay safely through escrow. Think Etsy meets a kennel club, with payment protection built in.",
  },
  thirty: {
    label: "30-second networking",
    duration: "≈ 30 seconds",
    body: "Buying a serious dog today — whether it's a working line German Shepherd, an American Bully, a rare Caucasian Ovcharka, or an exotic colored French Bulldog — runs on classified sites, social-media groups, and parking-lot meetups. Armstrong K9 Registry fixes that. We're a verified-breeder marketplace covering purebred AND exotic / specialty bloodlines, with built-in escrow payments, full pedigree trees with inbreeding warnings, breeder reviews, and a Buyer's Bill of Rights every purchase is protected by. We're a venue, not a seller — so the legal posture is clean and the trust layer is portable.",
  },
  sixty: {
    label: "60-second investor / partner",
    duration: "≈ 60 seconds",
    body: "Armstrong K9 Registry is a trust-and-safety platform for the purebred and exotic dog market — currently a multi-billion-dollar US industry that runs on classified sites, social-media groups, and word of mouth. We solve three big problems at once. First, discovery: a searchable marketplace where verified breeders and kennels publish health-tested, pedigreed listings — across both traditional purebred and exotic / specialty programs — with reviews, COI inbreeding warnings, and a Verified Standards badge for breeders who attest to our published guidelines. Second, payments: built-in Stripe escrow holds the buyer's money until they take delivery, so neither side has to trust a stranger with thousands of dollars upfront. Third, ecosystem: pedigree tracking, litter management, breeder networking, mating directory with a smart matchmaker that calculates COI and distance, plus events, marketing kits, and a buyer's bill of rights every transaction is gated by. We monetize through breeder subscriptions, transaction fees on escrow, and Buyer Pass premium memberships. Strategically positioned as a venue — like a verified-marketplace operator — so we set the standards that make the whole market safer.",
  },
  personal: {
    label: "Personal-story / press",
    duration: "≈ 45 seconds",
    body: "Anyone who has bought a serious dog — purebred or exotic — knows the experience: you scroll classified sites for weeks, drive three hours to meet a 'breeder' in a parking lot, hand over $2,500 in cash, and pray. There's no pedigree verification, no payment protection, no way to tell a hobby breeder from a puppy mill, and no recourse if the puppy turns out to be sick. Armstrong K9 Registry is what that market should have always been: verified breeders across every program from working line German Shepherds to rare exotic French Bulldogs, transparent bloodlines with inbreeding warnings, escrow that protects your money until the puppy arrives healthy, reviews from real buyers, and a Buyer's Bill of Rights every transaction is protected by. We're not the breeder. We're the venue that makes sure when you find one, you can trust them.",
  },
};

const TAGLINES = [
  "The trusted marketplace for purebred and exotic dogs.",
  "Find your breeder. Verify the pedigree. Pay with confidence.",
  "Where serious breeders meet serious buyers.",
  "Pedigree, protected.",
  "Every dog has a story. Every story has receipts.",
  "Built by breeders, for the buyers who actually research.",
];

// Pre-written launch copy for the Video Tours feature. Lightweight,
// editable in code — admin copies + pastes wherever they're posting.
const VT_SOCIAL = {
  twitter: `New on Armstrong K9 Registry: live 15-minute video tours with verified breeders. Meet the dog before you fly out. $35, only after the seller accepts. No-show? Full refund.

Browse: https://armstrongk9registry.com/video-tours

#DogBreeders #PurebredDog #ExoticBreeds`,
  instagram: `🎥 Now live: video tours with verified breeders.

Stop driving 6 hours to a parking lot. Stop wiring $2,500 to a stranger.

Book a 15-minute live video tour with the breeder. Walk through the kennel, meet the dog (and the parents), ask every question you'd ask in person — without the plane ticket.

✓ 15 minutes, browser-based (no install)
✓ $35, only after the seller accepts
✓ No-show? Full refund, automatic
✓ Verified breeders only — purebred AND exotic

Tap the link in bio to start.

#PurebredDog #ExoticBreeds #ResponsibleBreeding #DogBreeder`,
  facebook: `We just shipped what every serious dog buyer has been asking for: live video tours with verified breeders.

Here's how it works:
1) Find a dog on the marketplace you'd actually fly out for.
2) Suggest a 15-minute time slot. The seller has 24 hours to accept or counter.
3) Hop on a browser-based video call. Meet the dog. Meet the parents. Walk through the kennel. Ask every question you'd ask in person.

$35 booking fee — only charged if the seller accepts. No-show? Full refund, automatically.

This is the buyer-protection layer the dog market has been missing. Browse the marketplace at https://armstrongk9registry.com/video-tours.`,
  email_subject: "New on the registry: live video tours with verified breeders",
  email_preheader: "Meet the dog before you fly out. 15 minutes. $35. Only after the seller accepts.",
};

export default function PressKit() {
  const navigate = useNavigate();
  // "Back" should land the admin on the previous page in their
  // history, not on a fixed /admin destination — clicking deep links
  // into press kit (search, command bar, bookmarks) should bounce
  // them straight back to wherever they came from. Falls back to
  // /admin/console when there is no history entry behind us (e.g. a
  // bookmarked direct visit).
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/admin/console");
    }
  };
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Video Tours blast state
  const [vtBlast, setVtBlast] = useState(null); // null = loading, {} = never sent, {sent_at,...} = sent
  const [vtBusy, setVtBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/press/stats")
      .then((r) => setStats(r.data))
      .catch((e) => setError(e?.response?.status === 403 ? "forbidden" : "error"));
    api.get("/admin/marketing/video-tours/announce/status")
      .then((r) => setVtBlast(r.data?.blast || {}))
      .catch(() => setVtBlast({})); // soft-fail; default to "never sent"
  }, []);

  const sendVtBlast = async () => {
    if (!window.confirm(
      "This will email + inbox-message every non-admin user on the platform. " +
      "Marketing-opted-in only. Continue?"
    )) return;
    setVtBusy(true);
    try {
      const r = await api.post("/admin/marketing/video-tours/announce");
      setVtBlast(r.data?.blast || {});
      if (r.data?.already_sent) {
        toast.info("Already sent earlier — see the audit row below.");
      } else {
        toast.success(
          `Blast sent: ${r.data?.blast?.email_sent || 0} emails, ${r.data?.blast?.inbox_sent || 0} inbox.`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send blast.");
    } finally {
      setVtBusy(false);
    }
  };

  const resetVtBlast = async () => {
    if (!window.confirm("Clear the prior blast record so you can re-send? This won't unsend earlier emails.")) return;
    try {
      await api.delete("/admin/marketing/video-tours/announce");
      setVtBlast({});
      toast.success("Cleared. You can re-blast now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset.");
    }
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  const factSheet = useMemo(() => {
    if (!stats) return "";
    return [
      "Armstrong K9 Registry — Fact Sheet",
      "",
      "Verified-breeder marketplace + pedigree platform for purebred AND exotic dogs.",
      "Strategic posture: venue (not seller, breeder, or guarantor).",
      "",
      "Live registry footprint (as of " + new Date(stats.as_of).toLocaleString() + "):",
      `  • Registered dogs: ${stats.registered_dogs.toLocaleString()}`,
      `  • Breeds represented: ${stats.breeds_represented.toLocaleString()} (purebred + exotic / specialty)`,
      `  • Verified breeders: ${stats.verified_breeders.toLocaleString()}`,
      `  • Kennels enrolled: ${stats.registered_kennels.toLocaleString()}`,
      `  • Litters recorded: ${stats.litters_recorded.toLocaleString()}`,
      `  • Escrow transactions protected: ${stats.escrow_transactions_protected.toLocaleString()}`,
      `  • Total members: ${stats.total_members.toLocaleString()}`,
      "",
      "Key differentiators:",
      "  • Stripe-backed escrow (buyer money held until delivery)",
      "  • Coefficient of Inbreeding (COI) warnings at registration",
      "  • Verified Standards badge — breeders publicly attest to platform rules",
      "  • Buyer's Bill of Rights gated on every escrow checkout",
      "  • Smart Matchmaker with COI + distance + health scoring",
      "  • Inline CMS for legal docs (continuous compliance, no code deploys)",
      "  • Coverage across purebred AND exotic / specialty programs",
      "",
      "Press contact: support@armstrongk9registry.com",
    ].join("\n");
  }, [stats]);

  // Full plain-text bundle: fact sheet + every pitch + taglines + legal links.
  // Saved as a .txt the admin can email or attach to a press response.
  const fullBundle = useMemo(() => {
    const parts = [factSheet, "", "─".repeat(60), ""];
    parts.push("ELEVATOR PITCHES", "");
    Object.values(PITCHES).forEach((p) => {
      parts.push(`▸ ${p.label} (${p.duration})`);
      parts.push(p.body);
      parts.push("");
    });
    parts.push("─".repeat(60), "");
    parts.push("APPROVED TAGLINES", "");
    TAGLINES.forEach((t) => parts.push(`  • "${t}"`));
    parts.push("");
    parts.push("─".repeat(60), "");
    parts.push("PUBLIC LEGAL DOCUMENTS", "");
    [
      ["Terms of Service", "/terms"],
      ["Privacy Policy", "/privacy"],
      ["Refund Policy", "/refund"],
      ["Sub-processors", "/sub-processors"],
      ["Ethical Considerations", "/ethics"],
      ["Accessibility Statement", "/accessibility"],
      ["Security Measures", "/security"],
      ["Compliance Disclosures", "/compliance"],
      ["Buyer's Bill of Rights", "/buyer-rights"],
      ["Seller Standards", "/seller-standards"],
    ].forEach(([label, path]) => parts.push(`  • ${label} — ${webOrigin()}${path}`));
    return parts.join("\n");
  }, [factSheet]);

  const downloadBundle = () => {
    if (!stats) {
      toast.error("Stats are still loading — try again in a moment.");
      return;
    }
    const blob = new Blob([fullBundle], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `armstrong-press-kit-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Press kit downloaded");
  };

  const printPdf = () => {
    window.print();
  };

  if (error === "forbidden") {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="press-forbidden">
          <LockKey size={48} weight="duotone" className="text-crimson mx-auto mb-4" />
          <h1 className="font-spectral text-3xl tracking-tight mb-2">Admins only</h1>
          <p className="text-[14px] text-[#6B6B6B]">
            The press kit is internal — only admins can review it. If
            you believe you should have access, contact a registry admin.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="bg-[#FAFAFA] text-ink font-manrope min-h-full" data-testid="press-kit-page">
        {/* Action bar — never prints */}
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-6 print:hidden">
          <button
            type="button"
            onClick={goBack}
            className="text-[12px] text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1"
            data-testid="press-back-admin"
          >
            <ArrowRight size={11} className="rotate-180" /> Back
          </button>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-10 mt-4 mb-2 print:hidden flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[12px] text-[#6B6B6B]">
            <LockKey size={14} weight="duotone" className="text-gold" />
            <span>Admin-only · not publicly accessible · share at your discretion</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadBundle}
              className="inline-flex items-center gap-1.5 bg-white border border-[#0E0E0E] hover:bg-[#0E0E0E] hover:text-white rounded-full px-4 h-10 text-[13px] font-bold transition-colors"
              data-testid="press-download-bundle"
            >
              <DownloadSimple size={13} weight="bold" /> Download bundle (.txt)
            </button>
            <button
              type="button"
              onClick={printPdf}
              className="inline-flex items-center gap-1.5 bg-ink hover:bg-crimson text-white rounded-full px-4 h-10 text-[13px] font-bold transition-colors"
              data-testid="press-print-pdf"
            >
              <Printer size={13} weight="bold" /> Print / Save as PDF
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-[#0E0E0E] text-white mt-4" data-testid="press-hero">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-1.5">
              <Megaphone size={12} weight="fill" /> Press kit · admin-only resource
            </p>
            <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              The trusted marketplace for purebred and exotic dogs.
            </h1>
            <p className="text-[15px] md:text-[16px] leading-relaxed text-white/85 mt-5 max-w-3xl">
              Everything you need to talk about, partner with, or insure
              Armstrong K9 Registry — pitches, live stats,
              brand assets, legal documents, and our strategic posture as
              a venue serving both traditional purebred and exotic /
              specialty bloodlines. Download, print, or copy the pieces
              you want to share.
            </p>
          </div>
        </section>

        {/* Live stats */}
        {stats && (
          <section className="max-w-5xl mx-auto px-6 md:px-10 py-14" data-testid="press-stats">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-3 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> Registry footprint
            </p>
            <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight mb-2">
              Live numbers, pulled directly from the database.
            </h2>
            <p className="text-[13px] text-[#6B6B6B] mb-6">
              Refreshed every 5 minutes · as of {new Date(stats.as_of).toLocaleString()}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <Tile label="Registered dogs" value={stats.registered_dogs} icon={Dog} testId="press-stat-dogs" />
              <Tile label="Breeds (purebred + exotic)" value={stats.breeds_represented} icon={GlobeHemisphereWest} testId="press-stat-breeds" />
              <Tile label="Verified breeders" value={stats.verified_breeders} icon={ShieldCheck} testId="press-stat-verified" />
              <Tile label="Kennels enrolled" value={stats.registered_kennels} icon={Storefront} testId="press-stat-kennels" />
              <Tile label="Litters recorded" value={stats.litters_recorded} icon={GitFork} testId="press-stat-litters" />
              <Tile label="Escrow transactions" value={stats.escrow_transactions_protected} icon={Receipt} testId="press-stat-escrow" />
              <Tile label="Total members" value={stats.total_members} icon={Heart} testId="press-stat-members" />
              <Tile label="The Dog's Voice posts shared" value={stats.pawprints_shared} icon={Sparkle} testId="press-stat-pawprints" />
            </div>
          </section>
        )}

        {/* Pitches */}
        <section className="bg-white border-y border-[#E5E5E5]" data-testid="press-pitches">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-crimson mb-3 flex items-center gap-1.5">
              <Quotes size={12} weight="fill" /> Elevator pitches
            </p>
            <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight mb-2">
              Pick the length that fits the moment.
            </h2>
            <p className="text-[13px] text-[#6B6B6B] mb-6">
              All four versions are pre-written for journalists, investors,
              brokers, and quick networking. Click any pitch to copy.
            </p>

            <div className="space-y-4">
              {Object.entries(PITCHES).map(([key, p]) => (
                <article
                  key={key}
                  className="rounded-xl border border-[#E5E5E5] bg-white p-5"
                  data-testid={`press-pitch-${key}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <p className="font-bold text-ink">{p.label}</p>
                    <div className="flex items-center gap-3 text-[12px] text-[#6B6B6B]">
                      <span>{p.duration}</span>
                      <button
                        type="button"
                        onClick={() => copy(p.body, key)}
                        data-testid={`press-pitch-copy-${key}`}
                        className="inline-flex items-center gap-1 text-crimson font-bold hover:text-ink print:hidden"
                      >
                        {copiedId === key ? (
                          <><CheckCircle size={12} weight="fill" /> Copied</>
                        ) : (
                          <><CopySimple size={12} weight="bold" /> Copy</>
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="text-[14.5px] leading-[1.7] text-[#3F3F3F] whitespace-pre-wrap">
                    {p.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Taglines */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 py-14" data-testid="press-taglines">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#0F8043] mb-3 flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> Approved taglines
          </p>
          <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight mb-6">
            Six approved one-liners.
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {TAGLINES.map((t, i) => (
              <li
                key={i}
                data-testid={`press-tagline-${i}`}
                className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex items-center justify-between gap-3"
              >
                <p className="font-spectral text-[15px] text-ink tracking-tight flex-1">"{t}"</p>
                <button
                  type="button"
                  onClick={() => copy(t, `tagline-${i}`)}
                  className="text-[#6B6B6B] hover:text-ink flex-shrink-0 print:hidden"
                  data-testid={`press-tagline-copy-${i}`}
                >
                  {copiedId === `tagline-${i}` ? <CheckCircle size={14} weight="fill" className="text-[#0F8043]" /> : <CopySimple size={14} weight="bold" />}
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Video Tours launch kit — pre-written social copy + email blast */}
        <section className="bg-white border-y border-[#E5E5E5]" data-testid="press-vt-launch">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-crimson mb-3 flex items-center gap-1.5">
              <VideoCamera size={12} weight="fill" /> Feature launch · Live video tours
            </p>
            <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight mb-2">
              Announce video tours to the world.
            </h2>
            <p className="text-[13px] text-[#6B6B6B] mb-6 max-w-2xl">
              Pre-written copy for X, Instagram, and Facebook plus a one-click
              email blast that fires through Resend to every marketing-opted-in
              user. Idempotent — clicking twice won't double-send.
            </p>

            {/* Email blast control */}
            <div className="rounded-xl border-2 border-[#0E0E0E] bg-[#FBF7EC] p-5 mb-6" data-testid="vt-blast-card">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00] mb-1.5 flex items-center gap-1.5">
                    <EnvelopeSimple size={11} weight="fill" /> Email blast
                  </p>
                  <p className="font-bold text-ink">"{VT_SOCIAL.email_subject}"</p>
                  <p className="text-[12.5px] text-[#5C4A00] mt-1">
                    Sends to every non-admin, non-suspended user with broadcast
                    notifications enabled. Inbox + email. Returns counts.
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {vtBlast === null ? (
                    <span className="text-[12px] text-[#6B6B6B]">Loading…</span>
                  ) : vtBlast?.sent_at ? (
                    <>
                      <span
                        className="inline-flex items-center gap-1.5 bg-[#EEFBEC] border border-[#0F8043] text-[#0F8043] rounded-full px-3 py-1 text-[11px] font-bold"
                        data-testid="vt-blast-sent-badge"
                      >
                        <CheckCircle size={11} weight="fill" /> Sent
                      </span>
                      <button
                        type="button"
                        onClick={resetVtBlast}
                        className="text-[11px] text-crimson font-bold hover:text-ink"
                        data-testid="vt-blast-reset"
                      >
                        Reset / Re-blast
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={sendVtBlast}
                      disabled={vtBusy}
                      className="inline-flex items-center gap-1.5 bg-[#0E0E0E] hover:bg-[#C1272D] disabled:opacity-60 text-white rounded-full px-5 h-10 text-[13px] font-bold transition-colors"
                      data-testid="vt-blast-send"
                    >
                      <EnvelopeSimple size={13} weight="bold" />
                      {vtBusy ? "Sending…" : "Send blast now"}
                    </button>
                  )}
                </div>
              </div>
              {vtBlast?.sent_at && (
                <div className="text-[12px] text-[#5C4A00] grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t border-[#E5D08A]">
                  <div data-testid="vt-blast-stat-audience"><strong>Audience:</strong> {vtBlast.audience_total}</div>
                  <div data-testid="vt-blast-stat-email"><strong>Emails:</strong> {vtBlast.email_sent}</div>
                  <div data-testid="vt-blast-stat-inbox"><strong>Inbox:</strong> {vtBlast.inbox_sent}</div>
                  <div data-testid="vt-blast-stat-optedout"><strong>Opted out:</strong> {vtBlast.opted_out}</div>
                  {vtBlast.email_failed ? (
                    <div className="col-span-full text-crimson">
                      <strong>Email failures:</strong> {vtBlast.email_failed} (Resend retries automatically)
                    </div>
                  ) : null}
                  <div className="col-span-full text-[11px] text-[#8A6A00]">
                    Sent {new Date(vtBlast.sent_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            {/* Social copy cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="vt-social-cards">
              <SocialCard
                label="X / Twitter"
                accent="#0E0E0E"
                body={VT_SOCIAL.twitter}
                copyId="vt-tw"
                copiedId={copiedId}
                onCopy={() => copy(VT_SOCIAL.twitter, "vt-tw")}
              />
              <SocialCard
                label="Instagram"
                accent="#C1272D"
                body={VT_SOCIAL.instagram}
                copyId="vt-ig"
                copiedId={copiedId}
                onCopy={() => copy(VT_SOCIAL.instagram, "vt-ig")}
              />
              <SocialCard
                label="Facebook"
                accent="#3F4A8A"
                body={VT_SOCIAL.facebook}
                copyId="vt-fb"
                copiedId={copiedId}
                onCopy={() => copy(VT_SOCIAL.facebook, "vt-fb")}
              />
            </div>

            <div className="mt-5 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5] p-3 text-[12px] text-[#5C5C5C] flex items-center gap-2">
              <ShareNetwork size={13} weight="duotone" className="text-[#3F4A8A]" />
              <span>
                Visit the public landing page anytime at{" "}
                <Link to="/video-tours" className="text-crimson font-bold hover:text-ink" data-testid="vt-landing-link">
                  /video-tours
                </Link>{" "}
                — share that URL anywhere you post.
              </span>
            </div>
          </div>
        </section>

        {/* Brand assets */}
        <section className="bg-[#FBF7EC] border-y border-[#E5D08A]/50" data-testid="press-brand">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-3 flex items-center gap-1.5">
              <Fingerprint size={12} weight="fill" /> Brand
            </p>
            <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight mb-6">
              Logo, palette, typography.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-xl border border-[#E5D08A] bg-white p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00] mb-3">Primary mark</p>
                <div className="flex items-center justify-center bg-[#0E0E0E] rounded-lg h-44 mb-3">
                  <img src="/logo-256.png" alt="Logo" className="h-28 w-28 object-contain" data-testid="press-logo" />
                </div>
                <a href="/logo-256.png" download className="text-[12px] text-crimson font-bold inline-flex items-center gap-1 hover:text-ink print:hidden" data-testid="press-logo-download">
                  Download PNG <ArrowSquareOut size={11} />
                </a>
              </div>
              <div className="rounded-xl border border-[#E5D08A] bg-white p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00] mb-3">Color palette</p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Swatch hex="#0E0E0E" name="Onyx" />
                  <Swatch hex="#C9A227" name="Gold" />
                  <Swatch hex="#C1272D" name="Crimson" />
                  <Swatch hex="#FBF7EC" name="Parchment" />
                  <Swatch hex="#0F8043" name="Verdant" />
                  <Swatch hex="#FAFAFA" name="Bone" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00] mb-2 mt-4">Type</p>
                <p className="text-[13px] text-[#3F3F3F]"><strong>Headlines:</strong> Spectral · serif</p>
                <p className="text-[13px] text-[#3F3F3F]"><strong>Body:</strong> Manrope · sans</p>
              </div>
            </div>
          </div>
        </section>

        {/* Fact sheet + legal */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 py-14" data-testid="press-factsheet">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3F4A8A] mb-3 flex items-center gap-1.5">
            <Scales size={12} weight="fill" /> Fact sheet & legal
          </p>
          <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight mb-2">
            Everything in one place.
          </h2>
          <p className="text-[13px] text-[#6B6B6B] mb-6">
            Use this one-page fact sheet for any partner / broker / journalist
            conversation. Below it, links to every public legal artifact.
          </p>

          <div className="rounded-xl border border-[#E5E5E5] bg-white p-5 mb-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="font-bold text-ink">One-page fact sheet</p>
              <button
                type="button"
                onClick={() => copy(factSheet, "factsheet")}
                data-testid="press-factsheet-copy"
                className="text-crimson font-bold inline-flex items-center gap-1 hover:text-ink text-[12px] print:hidden"
              >
                {copiedId === "factsheet" ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><CopySimple size={12} weight="bold" /> Copy plain text</>}
              </button>
            </div>
            <pre className="text-[12px] text-[#3F3F3F] leading-[1.6] whitespace-pre-wrap font-mono bg-[#FAFAFA] border border-[#E5E5E5] rounded p-3 overflow-x-auto">
{factSheet || "Loading…"}
            </pre>
          </div>

          <p className="text-[12px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">Public legal documents</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <LegalLink to="/terms" label="Terms of Service" />
            <LegalLink to="/privacy" label="Privacy Policy" />
            <LegalLink to="/refund" label="Refund Policy" />
            <LegalLink to="/sub-processors" label="Sub-processors" />
            <LegalLink to="/ethics" label="Ethical Considerations" />
            <LegalLink to="/accessibility" label="Accessibility Statement" />
            <LegalLink to="/security" label="Security Measures" />
            <LegalLink to="/compliance" label="Compliance Disclosures" />
            <LegalLink to="/buyer-rights" label="Buyer's Bill of Rights" />
            <LegalLink to="/seller-standards" label="Seller Standards" />
          </ul>
        </section>

        {/* Posture */}
        <section className="bg-[#0E0E0E] text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
            <div className="flex items-center gap-3 mb-2">
              <Scales size={18} weight="duotone" className="text-gold" />
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                Strategic posture
              </p>
            </div>
            <h2 className="font-spectral text-2xl tracking-tight mb-3">
              We are a venue. Not a seller, breeder, or guarantor.
            </h2>
            <p className="text-[14.5px] leading-relaxed text-white/85">
              Every transaction is between a buyer and a breeder.
              Armstrong K9 Registry is the venue that
              connects them, sets the standards, and protects the
              payment via Stripe-backed escrow. We support both
              traditional purebred programs and exotic / specialty
              bloodlines on equal footing — every breeder operates
              under the same Verified Standards framework, and every
              buyer is protected by the same Bill of Rights.
            </p>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function Tile({ label, value, icon: Icon, testId }) {
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-3.5" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[#8B8B8B]">
        <Icon size={12} weight="duotone" />
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold">{label}</span>
      </div>
      <p className="font-spectral text-2xl tracking-tight text-ink mt-1">
        {Number(value || 0).toLocaleString()}
      </p>
    </div>
  );
}

function Swatch({ hex, name }) {
  return (
    <div className="text-center">
      <div className="rounded-md h-12 border border-[#E5E5E5] mb-1" style={{ background: hex }} />
      <p className="text-[10px] font-bold text-ink">{name}</p>
      <p className="text-[10px] text-[#6B6B6B] font-mono">{hex}</p>
    </div>
  );
}

function LegalLink({ to, label }) {
  return (
    <li>
      <Link
        to={to}
        target="_blank"
        rel="noreferrer"
        data-testid={`press-legal-${to.replace(/\//g, "-").replace(/^-/, "")}`}
        className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-2.5 text-[13px] text-ink hover:border-[#0E0E0E] flex items-center justify-between transition-colors"
      >
        <span>{label}</span>
        <ArrowSquareOut size={11} className="text-[#6B6B6B]" />
      </Link>
    </li>
  );
}

function SocialCard({ label, accent, body, copyId, copiedId, onCopy }) {
  return (
    <article
      className="rounded-xl border bg-white p-4 flex flex-col"
      style={{ borderColor: accent }}
      data-testid={`vt-social-${copyId}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <p
          className="text-[10px] uppercase tracking-[0.2em] font-bold"
          style={{ color: accent }}
        >
          {label}
        </p>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 text-[11px] font-bold hover:underline"
          style={{ color: accent }}
          data-testid={`vt-social-copy-${copyId}`}
        >
          {copiedId === copyId ? (
            <><CheckCircle size={11} weight="fill" /> Copied</>
          ) : (
            <><CopySimple size={11} weight="bold" /> Copy</>
          )}
        </button>
      </div>
      <pre className="text-[12px] leading-[1.55] text-[#3F3F3F] whitespace-pre-wrap font-manrope flex-1">
        {body}
      </pre>
    </article>
  );
}
